/**
 * Chrome DevTools-style range selector.
 *
 * A single horizontal track with two bracket handles framing a highlighted
 * selection. The user can drag either bracket independently, drag the middle
 * to pan the selection, scroll the timeline horizontally, or click the rail to
 * move the nearest bracket.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

type DragMode = 'left' | 'right' | 'pan'

export type RangeTimelineSliderProps = {
  /** Track start (epoch ms). */
  min: number
  /** Track end (epoch ms). */
  max: number
  /** Snap step (ms). */
  step: number
  /** Current selection [start, end] in epoch ms. */
  value: [number, number]
  /** Minimum allowed span (ms). */
  minSpan: number
  /** Maximum allowed span (ms). */
  maxSpan: number
  /** Called whenever the user drags. */
  onChange: (next: [number, number]) => void
  /** Called once when a pointer drag begins. */
  onDragStart?: (mode: DragMode) => void
  /** Called once when a pointer drag ends or is cancelled. */
  onDragEnd?: () => void
  /** Format epoch ms for axis tick labels (use a compact format). */
  formatTick: (epoch: number) => string
  /** Total height in px (default 64). */
  height?: number
  /**
   * Visible viewport span (ms). If unset or >= (max-min), the whole domain
   * fits and the track is not scrollable. If smaller, the inner track grows
   * wider and the outer container becomes horizontally scrollable.
   */
  viewSpan?: number
  /**
   * Explicit tick positions (epoch ms). If unset, defaults to [min, mid, max].
   */
  ticks?: number[]
}

type DragState = {
  mode: DragMode
  /** Drag anchor in scroll-container content coordinates (px, 0 at content left). */
  startContentX: number
  startValue: [number, number]
  /** Inner track width in px at drag start, stable for duration of drag. */
  trackWidth: number
  /** Most recent pointer client X, updated on every pointermove. */
  lastClientX: number
}

const AUTO_SCROLL_EDGE_PX = 18
const AUTO_SCROLL_MAX_SPEED = 10
const HANDLE_HITBOX_PX = 28
const SCROLLBAR_HIDE_PAD = 24
const MIN_TICK_LABEL_GAP_PX = 14

function estimateTickLabelWidth(label: string): number {
  return Math.max(62, Math.min(112, label.length * 7.1))
}

export default function RangeTimelineSlider({
  min,
  max,
  step,
  value,
  minSpan,
  maxSpan,
  onChange,
  onDragStart,
  onDragEnd,
  formatTick,
  height = 64,
  viewSpan,
  ticks,
}: RangeTimelineSliderProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const dragRef = useRef<DragState | null>(null)
  const rafRef = useRef<number | null>(null)
  const [viewportWidth, setViewportWidth] = useState(0)
  // Tracking re-render so cursor updates when drag mode changes.
  const [, setTick] = useState(0)
  const forceUpdate = useCallback(() => setTick((t) => t + 1), [])

  const range = Math.max(1, max - min)
  const [start, end] = value
  const pctStart = ((start - min) / range) * 100
  const pctEnd = ((end - min) / range) * 100
  const pctSpan = Math.max(0, pctEnd - pctStart)

  // Inner track width as a percentage of the visible viewport. If the caller
  // provided a `viewSpan` smaller than the full range, we widen the inner
  // track so that `viewSpan` ms occupies exactly 100% of the viewport.
  const innerWidthPct = viewSpan && viewSpan > 0 && viewSpan < range ? (range / viewSpan) * 100 : 100
  const effectiveTicks = useMemo(
    () => (ticks && ticks.length > 0 ? ticks : [min, Math.round((min + max) / 2), max]),
    [max, min, ticks],
  )

  const visibleTicks = useMemo(() => {
    const trackWidth = Math.max(1, (viewportWidth || 640) * (innerWidthPct / 100))
    const sortedTicks = [...effectiveTicks].filter((t) => t >= min && t <= max).sort((a, b) => a - b)
    const out: Array<{ t: number; label: string; pct: number; transform: string }> = []
    let lastRight = Number.NEGATIVE_INFINITY

    sortedTicks.forEach((t, i) => {
      const label = formatTick(t)
      const pct = ((t - min) / range) * 100
      const x = (pct / 100) * trackWidth
      const labelWidth = estimateTickLabelWidth(label)
      const isFirst = i === 0
      const isLast = i === sortedTicks.length - 1
      const transform = isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)'
      const left = isFirst ? x : isLast ? x - labelWidth : x - labelWidth / 2
      const right = left + labelWidth

      if (left >= lastRight + MIN_TICK_LABEL_GAP_PX) {
        out.push({ t, label, pct, transform })
        lastRight = right
      }
    })

    return out
  }, [effectiveTicks, formatTick, innerWidthPct, max, min, range, viewportWidth])

  const snap = useCallback((v: number) => Math.round(v / step) * step, [step])

  const clampPair = useCallback(
    (mode: DragMode, rawStart: number, rawEnd: number): [number, number] => {
      let s = snap(rawStart)
      let e = snap(rawEnd)
      if (mode === 'pan') {
        const span = e - s
        if (s < min) {
          s = min
          e = min + span
        }
        if (e > max) {
          e = max
          s = max - span
        }
      } else if (mode === 'left') {
        if (s < min) s = min
        if (s > e - minSpan) s = e - minSpan
        if (e - s > maxSpan) s = e - maxSpan
        if (s < min) s = min
      } else {
        if (e > max) e = max
        if (e < s + minSpan) e = s + minSpan
        if (e - s > maxSpan) e = s + maxSpan
        if (e > max) e = max
      }
      return [s, e]
    },
    [min, max, minSpan, maxSpan, snap],
  )

  const getContentX = useCallback((clientX: number): number => {
    const sc = scrollRef.current
    if (!sc) return clientX
    const rect = sc.getBoundingClientRect()
    return clientX - rect.left + sc.scrollLeft
  }, [])

  const contentXToValue = useCallback(
    (contentX: number, trackWidth: number): number => min + (contentX / Math.max(1, trackWidth)) * range,
    [min, range],
  )

  const applyDragRef = useRef<() => void>(() => {})
  applyDragRef.current = () => {
    const drag = dragRef.current
    if (!drag) return
    const contentX = getContentX(drag.lastClientX)
    const dx = contentX - drag.startContentX
    const dValue = (dx / drag.trackWidth) * range
    const [s0, e0] = drag.startValue
    let nextStart = s0
    let nextEnd = e0
    if (drag.mode === 'left') {
      nextStart = s0 + dValue
    } else if (drag.mode === 'right') {
      nextEnd = e0 + dValue
    } else {
      nextStart = s0 + dValue
      nextEnd = e0 + dValue
    }
    onChange(clampPair(drag.mode, nextStart, nextEnd))
  }

  const autoScrollTick = useCallback(() => {
    const drag = dragRef.current
    const sc = scrollRef.current
    if (!drag || !sc) {
      rafRef.current = null
      return
    }
    const rect = sc.getBoundingClientRect()
    const x = drag.lastClientX
    let speed = 0
    if (x < rect.left + AUTO_SCROLL_EDGE_PX) {
      speed = -Math.min(AUTO_SCROLL_MAX_SPEED, (rect.left + AUTO_SCROLL_EDGE_PX - x) * 0.35)
    } else if (x > rect.right - AUTO_SCROLL_EDGE_PX) {
      speed = Math.min(AUTO_SCROLL_MAX_SPEED, (x - (rect.right - AUTO_SCROLL_EDGE_PX)) * 0.35)
    }
    if (speed !== 0) {
      const maxScroll = sc.scrollWidth - sc.clientWidth
      const next = Math.max(0, Math.min(maxScroll, sc.scrollLeft + speed))
      if (next !== sc.scrollLeft) {
        sc.scrollLeft = next
        applyDragRef.current()
      }
    }
    rafRef.current = requestAnimationFrame(autoScrollTick)
  }, [])

  const beginDrag = useCallback(
    (mode: DragMode, event: React.PointerEvent, startValue: [number, number] = [value[0], value[1]]) => {
      if (!trackRef.current) return
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget.setPointerCapture(event.pointerId)
      dragRef.current = {
        mode,
        startContentX: getContentX(event.clientX),
        startValue,
        trackWidth: trackRef.current.clientWidth,
        lastClientX: event.clientX,
      }
      if (rafRef.current === null) {
        rafRef.current = requestAnimationFrame(autoScrollTick)
      }
      onDragStart?.(mode)
      forceUpdate()
    },
    [value, forceUpdate, getContentX, autoScrollTick, onDragStart],
  )

  const beginTrackDrag = useCallback(
    (event: React.PointerEvent) => {
      if (!trackRef.current) return
      const trackWidth = trackRef.current.clientWidth
      const raw = contentXToValue(getContentX(event.clientX), trackWidth)
      const mode: DragMode = Math.abs(raw - start) <= Math.abs(raw - end) ? 'left' : 'right'
      const next = mode === 'left' ? clampPair(mode, raw, end) : clampPair(mode, start, raw)
      beginDrag(mode, event, next)
      onChange(next)
    },
    [beginDrag, clampPair, contentXToValue, end, getContentX, onChange, start],
  )

  useEffect(() => {
    const sc = scrollRef.current
    if (!sc) return

    const update = () => setViewportWidth(sc.clientWidth)
    update()

    if (!globalThis.ResizeObserver) return
    const observer = new globalThis.ResizeObserver(update)
    observer.observe(sc)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const onMove = (e: globalThis.PointerEvent) => {
      const drag = dragRef.current
      if (!drag) return
      drag.lastClientX = e.clientX
      applyDragRef.current()
    }
    const onUp = () => {
      if (!dragRef.current) return
      dragRef.current = null
      onDragEnd?.()
      forceUpdate()
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [forceUpdate, onDragEnd])

  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    const sc = scrollRef.current
    if (!sc) return
    const onWheel = (e: globalThis.WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      if (sc.scrollWidth <= sc.clientWidth) return
      e.preventDefault()
      sc.scrollLeft += e.deltaY
    }
    sc.addEventListener('wheel', onWheel, { passive: false })
    return () => sc.removeEventListener('wheel', onWheel)
  }, [])

  useEffect(() => {
    if (dragRef.current) return
    const raf = requestAnimationFrame(() => {
      const sc = scrollRef.current
      if (!sc) return
      if (sc.scrollWidth <= sc.clientWidth) return
      const innerW = sc.scrollWidth
      const viewW = sc.clientWidth
      const startPx = ((value[0] - min) / range) * innerW
      const endPx = ((value[1] - min) / range) * innerW
      const centerPx = (startPx + endPx) / 2
      const next = Math.max(0, Math.min(innerW - viewW, centerPx - viewW / 2))
      sc.scrollLeft = next
    })
    return () => cancelAnimationFrame(raf)
  }, [value, min, range, innerWidthPct])

  const draggingPan = dragRef.current?.mode === 'pan'

  return (
    <div className="select-none overflow-hidden" style={{ height }}>
      <div
        ref={scrollRef}
        className="scrollbar-hide overflow-x-auto overflow-y-hidden"
        style={{ height: height + SCROLLBAR_HIDE_PAD }}
      >
        <div style={{ width: `${innerWidthPct}%` }}>
          <div className="relative h-5 text-[11px] text-gray-500 dark:text-slate-400">
            {visibleTicks.map(({ t, label, pct, transform }) => (
              <span key={t} className="absolute whitespace-nowrap" style={{ left: `${pct}%`, transform }}>
                {label}
              </span>
            ))}
          </div>

          <div
            ref={trackRef}
            className="relative h-9 touch-none rounded border border-gray-200 bg-gray-50 shadow-inner dark:border-gray-700 dark:bg-slate-800"
            onPointerDown={beginTrackDrag}
          >
            <div className="pointer-events-none absolute left-0 right-0 top-1/2 h-px -translate-y-1/2 bg-gray-200 dark:bg-slate-600" />
            <div
              className={
                'absolute bottom-0 top-0 touch-none rounded-sm border-y border-blue-500/30 bg-blue-100/70 dark:border-blue-300/20 dark:bg-blue-500/20 ' +
                (draggingPan ? 'cursor-grabbing' : 'cursor-grab')
              }
              style={{ left: `${pctStart}%`, width: `${pctSpan}%` }}
              onPointerDown={(e) => beginDrag('pan', e)}
            >
              <div
                className="absolute bottom-0 top-0 z-20 flex cursor-ew-resize touch-none items-center justify-center"
                style={{ left: 0, width: HANDLE_HITBOX_PX }}
                onPointerDown={(e) => beginDrag('left', e)}
              >
                <div className="absolute bottom-0 left-0 top-0 w-1 rounded-full bg-blue-600 shadow-sm ring-2 ring-white transition-transform hover:scale-x-125 dark:bg-blue-400 dark:ring-slate-900" />
              </div>
              <div
                className="absolute bottom-0 top-0 z-20 flex cursor-ew-resize touch-none items-center justify-center"
                style={{ right: 0, width: HANDLE_HITBOX_PX }}
                onPointerDown={(e) => beginDrag('right', e)}
              >
                <div className="absolute bottom-0 right-0 top-0 w-1 rounded-full bg-blue-600 shadow-sm ring-2 ring-white transition-transform hover:scale-x-125 dark:bg-blue-400 dark:ring-slate-900" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
